<script setup lang="ts">
import { computed, defineProps } from 'vue';
import Tag from 'primevue/tag';
import i18n from '@/plugins/i18n';
import { useUserManagement } from '@/composables/user-management';
import type { TagProps } from 'primevue/tag';
import { useUserInviteManagement } from '@/composables/user-invite-management';

// -----
// Props
// -----
const props = defineProps<{
  userId: string;
}>();

// Get user invite status information from the useUsers composable
const { isUserProfileCompleted } = useUserManagement(
  computed(() => props.userId),
);
const { hasNoInvite, hasExpiredInvite, hasPendingInvite } =
  useUserInviteManagement(computed(() => props.userId));

// -----
// Computed Properties
// -----
const statusType = computed(() => {
  if (hasNoInvite.value) return 'not_invited';
  if (hasExpiredInvite.value) return 'expired';
  if (hasPendingInvite.value) return 'invited';
  return isUserProfileCompleted.value ? 'complete' : 'incomplete';
});

const statusSeverity = computed<TagProps['severity']>(() => {
  switch (statusType.value) {
    case 'not_invited':
      return 'warning';
    case 'expired':
      return 'danger';
    case 'incomplete':
      return 'warning';
    case 'invited':
      return 'info';
    case 'complete':
      return 'success';
    default:
      return 'info';
  }
});

const statusText = computed(() => {
  switch (statusType.value) {
    case 'not_invited':
      return i18n.t('teamPage.status.notInvited');
    case 'expired':
      return i18n.t('teamPage.status.inviteExpired');
    case 'invited':
      return i18n.t('teamPage.status.inviteSent');
    case 'complete':
      return i18n.t('teamPage.status.profileComplete');
    case 'incomplete':
      return i18n.t('teamPage.status.profileIncomplete');
    default:
      return '';
  }
});
</script>

<template>
  <Tag :severity="statusSeverity" :value="statusText" :class="$style.tag" />
</template>

<style module>
.tag {
  white-space: nowrap;
}
</style>
